import { useSignIn } from '@shared/auth';
import { Button, Skeleton } from '@shared/components/shadcn';
import { ActiveTrue } from '@shared/icons';
import { Location } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useMultistepForm } from '../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { formatAddress, ReKycStepFooter, ReKycStepHeader } from '../../common';
import type { useReKyc } from '../../hooks/useReKyc';

type ConfirmAddressStepProps = ReturnType<typeof useReKyc>;

function ConfirmAddressStep({ query, confirmMutation, updateMutation }: ConfirmAddressStepProps) {
  const { goToNextStep } = useMultistepForm();
  const pathname = usePathname();
  const signIn = useSignIn();
  const t = useTranslations('my_account.rekyc');
  return (
    <>
      <ReKycStepHeader
        headerIcon={<Location size="extra-extra-large" data-testid="rekyc-confirm-address-icon" />}
        title={<>{t('confirm_step.title')}</>}
        description={t('confirm_step.description')}
      />
      <div className="flex items-center gap-4 rounded bg-container p-4">
        <ActiveTrue className="shrink-0" />
        {query.data ? (
          <p data-testid="confirm-address-text" data-track-privacy="Mask">
            {formatAddress(query.data)}
          </p>
        ) : (
          // TODO: No design exists for the GET error state — buttons remain disabled on failure
          <Skeleton className="inline-block h-[1em] w-full align-middle" />
        )}
      </div>
      <ReKycStepFooter
        actions={
          <>
            <Button
              variant="secondary"
              onClick={goToNextStep}
              disabled={!query.data || confirmMutation.isPending || updateMutation.isPending}
              data-track-action="Update"
              data-testid="rekyc-update-button"
            >
              {t('confirm_step.update')}
            </Button>
            <Button
              variant="primary"
              data-testid="rekyc-confirm-button"
              onClick={() => {
                void confirmMutation.mutateAsync().then(
                  () => window.location.reload(),
                  () => signIn({ callbackUrl: pathname })
                );
              }}
              disabled={!query.data || confirmMutation.isPending || updateMutation.isPending}
              data-track-action="Confirm"
            >
              {t('confirm_step.confirm')}
            </Button>
          </>
        }
      />
    </>
  );
}

export { ConfirmAddressStep, type ConfirmAddressStepProps };
